import { useEffect, useMemo, useRef, useState } from "react";
import { useAppStore } from "../store/useAppStore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Activity, AlertCircle, Filter, Navigation, RotateCcw, Search, ShieldAlert, Sparkles } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { haversineDistance } from "../lib/geo";
import { geocodeAddress } from "../lib/geocoding";
import { toast } from "sonner";
import { HeatmapLayer } from "../components/HeatmapLayer";
import { PropertySnapshotCard } from "../components/discovery/PropertySnapshotCard";
import type { Lead, Opportunity } from "../types";

// Fix for default leaflet marker icon in React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const defaultCenter: [number, number] = [39.8283, -98.5795]; // center of USA

type MapDiscoveryItem = (Lead & { type: "lead" }) | (Opportunity & { type: "opportunity" });

function MapUpdater({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();

  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);

  return null;
}

function buildFocusedIcon(isLead: boolean) {
  const color = isLead ? "#2563eb" : "#10b981";
  const glow = isLead ? "rgba(37, 99, 235, 0.22)" : "rgba(16, 185, 129, 0.22)";

  return L.divIcon({
    className: "map-focused-marker",
    html: `<div style="position:relative;width:22px;height:22px;display:flex;align-items:center;justify-content:center;">
      <span style="position:absolute;inset:-8px;border-radius:9999px;background:${glow};"></span>
      <span style="position:relative;width:18px;height:18px;border-radius:9999px;background:${color};border:3px solid white;box-shadow:0 10px 24px rgba(15,23,42,0.2);"></span>
    </div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -10],
  });
}

function DiscoveryMarker({
  item,
  isFocused,
  isConverting,
  onCenterHere,
  onConvertToLead,
  onOpenLead,
}: {
  item: MapDiscoveryItem;
  isFocused: boolean;
  isConverting: boolean;
  onCenterHere: (item: MapDiscoveryItem) => void;
  onConvertToLead: (item: Opportunity) => void;
  onOpenLead: (leadId: string) => void;
}) {
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (isFocused) {
      markerRef.current?.openPopup();
    }
  }, [isFocused]);

  const focusedIcon = isFocused ? buildFocusedIcon(item.type === "lead") : undefined;

  return (
    <Marker ref={markerRef} position={[item.lat!, item.lng!]} icon={focusedIcon}>
      <Popup className="rounded-xl overflow-hidden min-w-[320px]">
        {item.type === "lead" ? (
          <PropertySnapshotCard
            tone="lead"
            address={item.propertyAddress}
            sourceLabel={String((item as any).leadSource || (item as any).source || "Lead")}
            signalLabel={String((item as any).status || "Lead")}
            summary={[(item as any).sellerName, (item as any).phone].filter(Boolean).join(" · ") || undefined}
            tags={item.tags || []}
            metaLabel={isFocused ? "Focused on map" : undefined}
            primaryAction={{
              label: "View Lead Details",
              onClick: () => onOpenLead(item.id),
            }}
            secondaryAction={{
              label: "Center Here",
              variant: "outline",
              onClick: () => onCenterHere(item),
            }}
          />
        ) : (
          <PropertySnapshotCard
            tone="opportunity"
            address={item.propertyAddress}
            sourceLabel={item.source === "public_data" ? "Public Data" : item.source === "csv" ? "CSV Import" : item.source}
            signalLabel={`Score: ${(item as any).opportunityScore}`}
            summary={(item as any).notes || undefined}
            tags={item.tags || []}
            metaLabel={isFocused ? "Focused on map" : formatDistanceToNow(parseISO((item as any).savedAt)) + " ago"}
            primaryAction={{
              label: isConverting ? "Converting..." : "Convert to Lead",
              disabled: isConverting,
              onClick: () => onConvertToLead(item),
            }}
            secondaryAction={{
              label: "Center Here",
              variant: "outline",
              onClick: () => onCenterHere(item),
            }}
          />
        )}
      </Popup>
    </Marker>
  );
}

export default function MapPage() {
  const { leads, opportunities } = useAppStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [searchAddress, setSearchAddress] = useState("");
  const [radius, setRadius] = useState<string>(searchParams.get("radius") || "all");
  const [isSearching, setIsSearching] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [convertingOpportunityId, setConvertingOpportunityId] = useState<string | null>(null);

  const initialLat = searchParams.get("lat");
  const initialLng = searchParams.get("lng");
  const focusId = searchParams.get("itemId") || searchParams.get("opportunityId") || searchParams.get("leadId");

  const [currentCenter, setCurrentCenter] = useState<[number, number] | null>(
    initialLat && initialLng ? [parseFloat(initialLat), parseFloat(initialLng)] : null,
  );
  const [zoom, setZoom] = useState(initialLat && initialLng ? 14 : 4);
  const hasAutoCenteredRef = useRef(false);

  const allItemsRaw = useMemo<MapDiscoveryItem[]>(
    () => [
      ...leads.filter((lead) => typeof lead.lat === "number" && typeof lead.lng === "number").map((lead) => ({ ...lead, type: "lead" as const })),
      ...opportunities
        .filter((opportunity) => typeof opportunity.lat === "number" && typeof opportunity.lng === "number")
        .map((opportunity) => ({ ...opportunity, type: "opportunity" as const })),
    ],
    [leads, opportunities],
  );

  const visibleItems = useMemo(() => {
    if (radius === "all" || !currentCenter) {
      return allItemsRaw;
    }

    const radiusMiles = parseFloat(radius);
    if (!Number.isFinite(radiusMiles)) {
      return allItemsRaw;
    }

    return allItemsRaw.filter((item) => {
      const dist = haversineDistance(currentCenter, [item.lat!, item.lng!]);
      return dist <= radiusMiles;
    });
  }, [allItemsRaw, currentCenter, radius]);

  const focusItem = useMemo(() => {
    if (!focusId) return undefined;
    return allItemsRaw.find((item) => item.id === focusId);
  }, [allItemsRaw, focusId]);

  const leadCount = visibleItems.filter((item) => item.type === "lead").length;
  const opportunityCount = visibleItems.filter((item) => item.type === "opportunity").length;
  const hasAnyMappedItems = allItemsRaw.length > 0;
  const hasVisibleItems = visibleItems.length > 0;
  const isRadiusActive = radius !== "all" && Boolean(currentCenter);

  useEffect(() => {
    if (focusItem?.lat == null || focusItem?.lng == null) return;
    setCurrentCenter([focusItem.lat, focusItem.lng]);
    setZoom(15);
  }, [focusItem]);

  useEffect(() => {
    if (focusId || hasAutoCenteredRef.current || currentCenter || allItemsRaw.length === 0) {
      return;
    }

    const firstItem = allItemsRaw[0];
    if (firstItem?.lat != null && firstItem?.lng != null) {
      hasAutoCenteredRef.current = true;
      setCurrentCenter([firstItem.lat, firstItem.lng]);
      setZoom(12);
    }
  }, [allItemsRaw, currentCenter, focusId]);

  const handleSearch = async () => {
    if (!searchAddress) return;
    setIsSearching(true);
    try {
      const result = await geocodeAddress(searchAddress);
      if (result) {
        setCurrentCenter([result.lat, result.lng]);
        setZoom(13);
        toast.success(`Centered on ${searchAddress}${radius !== "all" ? ` within ${radius} miles` : ""}.`);
      } else {
        toast.error("Location not found");
      }
    } catch (e) {
      toast.error("Failed to geocode address");
    } finally {
      setIsSearching(false);
    }
  };

  const handleResetView = () => {
    setSearchAddress("");
    setRadius("all");
    setCurrentCenter(null);
    setZoom(4);
  };

  const handleCenterItem = (item: MapDiscoveryItem) => {
    setCurrentCenter([item.lat!, item.lng!]);
    setZoom(15);
    setRadius("1");
  };

  const handleConvertOpportunity = async (item: Opportunity) => {
    setConvertingOpportunityId(item.id);
    try {
      await useAppStore.getState().addLead({
        sellerName: "PUBLIC RECORD OWNER",
        phone: "",
        propertyAddress: item.propertyAddress,
        askingPrice: null,
        motivation: "Public distress signal detected",
        status: "New Lead",
        leadSource: (item as any).source || "manual",
        tags: ["arcgis", "property-data", "needs-skiptrace", ...(item.tags || [])],
        notes: `${(item as any).notes || ""}\nConverted from Opportunity Score: ${(item as any).opportunityScore}`,
      });

      const userId = useAppStore.getState().userId;
      if (userId) {
        useAppStore.setState((state) => ({ opportunities: state.opportunities.filter((opportunity) => opportunity.id !== item.id) }));
        import("firebase/firestore").then(({ deleteDoc, doc }) => {
          import("../lib/firebase").then(({ db }) => {
            void deleteDoc(doc(db, `users/${userId}/opportunities/${item.id}`));
          });
        });
      }
    } catch (error) {
      toast.error("Failed to convert opportunity");
    } finally {
      setConvertingOpportunityId(null);
    }
  };

  const handleOpenLead = (leadId: string) => {
    navigate(`/leads/${leadId}`);
  };

  const centerToUse = currentCenter || defaultCenter;
  const heatmapPoints = visibleItems
    .filter((item) => item.type === "opportunity")
    .map((item) => [item.lat!, item.lng!, (((item as Opportunity).opportunityScore || 0) / 100) * 1.5] as [number, number, number]);

  return (
    <div className="mx-auto flex h-full w-full max-w-7xl flex-col gap-6 pb-10">
      <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm shadow-slate-900/5">
        <div className="grid gap-6 px-5 py-6 sm:px-8 lg:grid-cols-[1.05fr_.95fr] lg:items-end lg:px-10 lg:py-8">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
              <span>Map intelligence</span>
              <span className="h-1 w-1 rounded-full bg-slate-300" />
              <span>Geographic view</span>
            </div>
            <div className="space-y-3">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl flex items-center gap-3">
                <Navigation className="h-6 w-6 text-blue-600" /> Deal Map
              </h1>
              <p className="max-w-2xl text-sm leading-6 text-slate-500">Discover nearby deals, public data signals, and active leads.</p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs font-medium text-slate-600">
              <span className="rounded-full bg-slate-100 px-3 py-1.5">{allItemsRaw.length} mapped</span>
              <span className="rounded-full bg-blue-50 px-3 py-1.5 text-blue-700">{leadCount} leads</span>
              <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-emerald-700">{opportunityCount} opportunities</span>
              <span className="rounded-full bg-amber-50 px-3 py-1.5 text-amber-700">{isRadiusActive ? `Radius ${radius} mi` : "No radius filter"}</span>
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-stretch">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search Address or City..."
                className="pl-9 h-11 rounded-2xl border-slate-200 bg-slate-50 shadow-sm"
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Select value={radius} onValueChange={setRadius}>
                <SelectTrigger className="w-[140px] h-11 rounded-2xl border-slate-200 bg-white shadow-sm" aria-label="Map radius filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Radius" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everywhere</SelectItem>
                  <SelectItem value="1">Within 1 mile</SelectItem>
                  <SelectItem value="5">Within 5 miles</SelectItem>
                  <SelectItem value="10">Within 10 miles</SelectItem>
                  <SelectItem value="50">Within 50 miles</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} disabled={isSearching || !searchAddress} className="h-11 rounded-2xl bg-slate-950 px-5 shadow-sm hover:bg-slate-800">
                {isSearching ? "Searching..." : "Search"}
              </Button>
              <Button variant="outline" onClick={handleResetView} className="h-11 gap-2 rounded-2xl border-slate-200 bg-slate-50 px-4 shadow-sm hover:bg-slate-100">
                <RotateCcw className="w-4 h-4" /> Reset View
              </Button>
              <Button
                variant={showHeatmap ? "default" : "outline"}
                onClick={() => setShowHeatmap(!showHeatmap)}
                aria-pressed={showHeatmap}
                aria-label={showHeatmap ? "Disable heatmap overlay" : "Enable heatmap overlay"}
                className={`h-11 gap-2 rounded-2xl px-4 shadow-sm ${showHeatmap ? "bg-orange-500 hover:bg-orange-600" : "border-slate-200 bg-white hover:bg-slate-50"}`}
              >
                <Activity className="w-4 h-4" /> {showHeatmap ? "Heatmap On" : "Heatmap Off"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <Navigation className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Mapped</p>
              <p className="text-2xl font-bold text-slate-900">{allItemsRaw.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Visible</p>
              <p className="text-2xl font-bold text-slate-900">{visibleItems.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Radius</p>
              <p className="text-2xl font-bold text-slate-900">{isRadiusActive ? `${radius} mi` : "All"}</p>
              <p className="text-xs text-slate-500">Map filter</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Center</p>
              <p className="text-2xl font-bold text-slate-900 truncate">{focusItem ? focusItem.propertyAddress : currentCenter ? "Selected location" : "USA"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">Map controls</p>
              <p className="text-sm text-slate-500">Use search, radius filters, or the heatmap overlay to zoom in on the best opportunities faster.</p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs text-slate-500">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{leadCount} leads</Badge>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{opportunityCount} opportunities</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex-1 relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 shadow-sm">
        <MapContainer center={centerToUse} zoom={zoom} className="h-full min-h-[560px] w-full">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          />
          <MapUpdater center={centerToUse} zoom={zoom} />

          {showHeatmap && heatmapPoints.length > 0 && <HeatmapLayer points={heatmapPoints} />}

          {!showHeatmap &&
            visibleItems.map((item) => (
              <DiscoveryMarker
                key={item.id}
                item={item}
                isFocused={item.id === focusItem?.id}
                isConverting={item.type === "opportunity" && convertingOpportunityId === item.id}
                onCenterHere={handleCenterItem}
                onConvertToLead={handleConvertOpportunity}
                onOpenLead={handleOpenLead}
              />
            ))}
        </MapContainer>

        {!hasVisibleItems ? (
          <div className="absolute inset-0 z-[401] flex items-center justify-center p-6 pointer-events-none">
            <div className="pointer-events-auto w-full max-w-xl rounded-3xl border border-slate-200 bg-white/95 p-6 shadow-xl backdrop-blur-sm">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
                  <ShieldAlert className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <p className="text-lg font-semibold text-slate-900">{hasAnyMappedItems ? "No properties match this view" : "No mapped properties yet"}</p>
                  <p className="mt-1 text-sm text-slate-600">
                    {hasAnyMappedItems
                      ? "Try widening the radius, clearing the address search, or resetting the map view."
                      : "Save opportunities or leads with geocodes to see them on the map and explore nearby deals."}
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <Button onClick={handleResetView} variant="outline" className="gap-2 rounded-2xl border-slate-200">
                      <RotateCcw className="h-4 w-4" /> Reset filters
                    </Button>
                    <Button onClick={() => navigate("/opportunities")} className="gap-2 rounded-2xl bg-emerald-600 hover:bg-emerald-700">
                      <Activity className="h-4 w-4" /> Browse opportunities
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
